import React, { useEffect, useState } from "react";
import { Calendar, momentLocalizer } from "react-big-calendar";
import "react-big-calendar/lib/css/react-big-calendar.css";
import moment from "moment";
import { useNavigate } from "react-router-dom";
import Select from "react-select";
import {
  getEventsByUserId,
  getEventDetailsByEventID,
  getCurrentUserDetails,
  getAllEvents,
  getAllBoatOwners,
  getAllUsersDetails,
} from "../utils";
import { useAuth } from "../../contexts/AuthContext";
import { toast } from "react-toastify";

const localizer = momentLocalizer(moment);

const selectStyles = {
  menuPortal: (base) => ({ ...base, zIndex: 99999 }),
  menu: (base) => ({ ...base, zIndex: 99999 }),
};

const matchesStatus = (event, status) => {
  const bookingStatus = (event.bookingStatus || "").toLowerCase();

  switch (status) {
    case "Pending":
      return event.color === "red";
    case "Waivers Pending":
      return event.color === "#651FFF";
    case "Waivers Filled":
      return event.color === "#2196F3";
    case "Trip Started":
      return event.color === "orange";
    case "Trip Returned":
      return (
        bookingStatus === "trip returned" || bookingStatus === "trip completed"
      );
    case "Finished":
      // Only after hold is cancelled/released (or damage charged)
      return bookingStatus === "trip concluded";
    case "Trip Cancelled":
      return event.color === "#E843C3";
    default:
      return true;
  }
};

const getUserDisplayName = (user) =>
  `${user.firstName || ""} ${user.lastName || ""}`.trim() ||
  user.email ||
  user.userId;

const buildUserOption = (user, bookingCount = 0) => ({
  value: user.userId,
  bookingCount,
  label: `${getUserDisplayName(user)} (${bookingCount})`,
});

const countBookingsByField = (events, field) => {
  const counts = {};
  events.forEach((event) => {
    const id = event?.inquireInfo?.[field];
    if (id) {
      counts[id] = (counts[id] || 0) + 1;
    }
  });
  return counts;
};

const sortByBookingCount = (a, b) =>
  b.bookingCount - a.bookingCount || a.label.localeCompare(b.label);

export default function Bookings() {
  const auth = useAuth();
  const currentUser = auth.currentUser;

  const Navigate = useNavigate();

  const [calendarEvents, setCalendarEvents] = useState([]);
  const [allEvents, setAllEvents] = useState([]);
  const [activeFilter, setActiveFilter] = useState("All");
  const [isAdmin, setIsAdmin] = useState(false);
  const [selectedOwner, setSelectedOwner] = useState(null);
  const [selectedRenter, setSelectedRenter] = useState(null);
  const [ownerOptions, setOwnerOptions] = useState([]);
  const [renterOptions, setRenterOptions] = useState([]);

  const applyFilters = (events, status, owner, renter) => {
    let filtered = events;

    if (status && status !== "All") {
      filtered = filtered.filter((event) => matchesStatus(event, status));
    }

    if (owner?.value) {
      filtered = filtered.filter(
        (event) => event.inquireInfo?.owner === owner.value
      );
    }

    if (renter?.value) {
      filtered = filtered.filter(
        (event) => event.inquireInfo?.renter === renter.value
      );
    }

    setCalendarEvents(filtered);
  };

  useEffect(() => {
    const timezone = moment.tz.guess();
    const fetchEvents = async () => {
      try {
        const userDetails = await getCurrentUserDetails();

        if (
          userDetails.role === "Boat Owner" ||
          userDetails.role === "Renter"
        ) {
          setIsAdmin(false);
          const individualEvents = await getEventsByUserId(currentUser.uid);

          const filteredEvents = individualEvents.map((event) => {
            const endMoment = moment(event.end).tz(timezone);
            if (parseInt(event?.overNight, 10) > 0) {
              return {
                ...event,
                start: new Date(event.start),
                end: endMoment.toDate(),
              };
            }
            if (
              event?.inquireInfo?.offerData &&
              parseInt(event?.inquireInfo?.offerData?.offeredHours, 10) > 0
            ) {
              const startDate = new Date(event.start);

              return {
                ...event,
                start: startDate,
                end: endMoment.toDate(),
              };
            }

            return {
              ...event,
              start: new Date(event.start),
              end: endMoment.toDate(),
            };
          });

          setCalendarEvents(filteredEvents);
          setAllEvents(filteredEvents);
        } else if (userDetails.role === "Admin") {
          setIsAdmin(true);
          const [individualEvents, boatOwners, allUsers] = await Promise.all([
            getAllEvents(currentUser.uid),
            getAllBoatOwners(),
            getAllUsersDetails(),
          ]);

          const filteredEvents = individualEvents.map((event) => {
            const endMoment = moment(event.end).tz(timezone);
            if (parseInt(event.overNight, 10) > 0) {
              return {
                ...event,
                start: new Date(event.start),
                end: endMoment.toDate(),
              };
            }

            if (
              event?.inquireInfo?.offerData !== undefined &&
              parseInt(event?.inquireInfo?.offerData?.offeredHours, 10) > 0
            ) {
              return {
                ...event,
                start: new Date(event.start),
                end: endMoment.toDate(),
              };
            }

            return {
              ...event,
              start: new Date(event.start),
              end: endMoment.toDate(),
            };
          });

          const ownerCounts = countBookingsByField(filteredEvents, "owner");
          const renterCounts = countBookingsByField(filteredEvents, "renter");

          setOwnerOptions(
            (boatOwners || [])
              .filter((owner) => !owner.isDeleted)
              .map((owner) =>
                buildUserOption(owner, ownerCounts[owner.userId] || 0)
              )
              .sort(sortByBookingCount)
          );

          setRenterOptions(
            (allUsers || [])
              .filter((user) => user.role === "Renter" && !user.isDeleted)
              .map((user) =>
                buildUserOption(user, renterCounts[user.userId] || 0)
              )
              .sort(sortByBookingCount)
          );

          setCalendarEvents(filteredEvents);
          setAllEvents(filteredEvents);
        }
      } catch (error) {
        console.error("Error fetching events:", error);
        setCalendarEvents([]);
      }
    };

    fetchEvents();
  }, [currentUser]);

  const filterEvents = (status) => {
    const nextStatus = status || "All";
    setActiveFilter(nextStatus);
    applyFilters(allEvents, nextStatus, selectedOwner, selectedRenter);
  };

  const handleOwnerChange = (option) => {
    setSelectedOwner(option);
    applyFilters(allEvents, activeFilter, option, selectedRenter);
  };

  const handleRenterChange = (option) => {
    setSelectedRenter(option);
    applyFilters(allEvents, activeFilter, selectedOwner, option);
  };

  const eventStyleGetter = (event, start, end, isSelected) => {
    const style = {
      backgroundColor: event.color,
      borderRadius: "5px",
      color: "white",
      border: "1px solid #ccc",
      display: "block",
      cursor: "pointer",
    };

    return {
      style,
    };
  };

  const handleEventClick = (event) => {
    const matchingEvent = calendarEvents.find(
      (calEvent) => calEvent.id === event.id
    );

    const selectedEvent = getEventDetailsByEventID(event.id);

    if (matchingEvent?.inquireInfo !== undefined) {
      Navigate(`/dashboard/event/${event.id}`);
    } else {
      toast.error("This inquiry is currently awaiting review.");
    }
  };
  return (
    <>
      <div className="row">
        <div className="col">
          <div className="page-description">
            <h1>Bookings</h1>
          </div>
        </div>
      </div>
      <BookingIndexes onFilterChange={filterEvents} activeFilter={activeFilter} />

      {isAdmin && (
        <div className="booking-user-filters mb-3">
          <div className="booking-user-filter">
            <label className="form-label mb-1" htmlFor="boat-owner-filter">
              Boat Owner
            </label>
            <Select
              inputId="boat-owner-filter"
              isClearable
              isSearchable
              options={ownerOptions}
              value={selectedOwner}
              onChange={handleOwnerChange}
              placeholder="Filter by boat owner..."
              menuPortalTarget={document.body}
              menuPosition="fixed"
              styles={selectStyles}
            />
          </div>
          <div className="booking-user-filter">
            <label className="form-label mb-1" htmlFor="renter-filter">
              Renter
            </label>
            <Select
              inputId="renter-filter"
              isClearable
              isSearchable
              options={renterOptions}
              value={selectedRenter}
              onChange={handleRenterChange}
              placeholder="Filter by renter..."
              menuPortalTarget={document.body}
              menuPosition="fixed"
              styles={selectStyles}
            />
          </div>
        </div>
      )}

      {console.log("calendarEvents2123", calendarEvents)}

      <div className="row">
        <div className="col">
          <div className="card calendar-container">
            <div className="card-body cal-body">
              <Calendar
                localizer={localizer}
                views={["month", "week", "day"]}
                events={calendarEvents || []}
                startAccessor="start"
                endAccessor="end"
                eventPropGetter={eventStyleGetter}
                onSelectEvent={handleEventClick}
                popup
                // style={{ height: 550 }}
                popupOffset={{ x: 5, y: 5 }}
                showAllEvents={false}
                events_limit={2}
                messages={{
                  showMore: (total) => `+${total} more`,
                }}
              />
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

const BookingIndexes = ({ onFilterChange, activeFilter }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  // Define button styles
  const getButtonStyle = (status) => {
    const baseStyle = {
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      border: '2px solid transparent',
    };

    // Add active state styling
    if (activeFilter === status) {
      return {
        ...baseStyle,
        border: '2px solid #000',
        boxShadow: '0 0 5px rgba(0,0,0,0.3)',
        transform: 'scale(1.05)',
      };
    }

    return baseStyle;
  };

  return (
    <div className="booking-legend-container mb-3">
      <div
        className="labels-wrapper align-items-center booking-labels-info d-flex"
      >
        <span
          className="color-box text-center"
          style={{
            backgroundColor: "#6c757d",
            ...getButtonStyle("All")
          }}
          onClick={() => onFilterChange("All")}
        >
          All
        </span>
        <span
          className="color-box text-center"
          style={{
            backgroundColor: "rgb(255 0 0 / 70%)",
            ...getButtonStyle("Pending")
          }}
          onClick={() => onFilterChange(activeFilter === "Pending" ? "All" : "Pending")}
        >
          Pending
        </span>
        <span
          className="color-box text-center"
          style={{
            backgroundColor: "rgb(101 31 255 / 70%)",
            ...getButtonStyle("Waivers Pending")
          }}
          onClick={() => onFilterChange(activeFilter === "Waivers Pending" ? "All" : "Waivers Pending")}
        >
          Waivers Pending
        </span>
        <span
          className="color-box text-center"
          style={{
            backgroundColor: "rgb(33 150 243 / 70%)",
            ...getButtonStyle("Waivers Filled")
          }}
          onClick={() => onFilterChange(activeFilter === "Waivers Filled" ? "All" : "Waivers Filled")}
        >
          Waivers Filled
        </span>
        <span
          className="color-box text-center"
          style={{
            backgroundColor: "rgb(255 165 0 / 70%)",
            ...getButtonStyle("Trip Started")
          }}
          onClick={() => onFilterChange(activeFilter === "Trip Started" ? "All" : "Trip Started")}
        >
          Trip Started
        </span>
        <span
          className="color-box text-center"
          style={{
            backgroundColor: "rgb(49 49 48 / 62%)",
            ...getButtonStyle("Trip Returned")
          }}
          onClick={() => onFilterChange(activeFilter === "Trip Returned" ? "All" : "Trip Returned")}
        >
          Trip Returned
        </span>
        <span
          className="color-box text-center"
          style={{
            backgroundColor: "rgb(0 128 0 / 70%)",
            ...getButtonStyle("Finished")
          }}
          onClick={() => onFilterChange(activeFilter === "Finished" ? "All" : "Finished")}
        >
          Trip Finished
        </span>
        <span
          className="color-box text-center"
          style={{
            backgroundColor: "#E843C3",
            ...getButtonStyle("Trip Cancelled")
          }}
          onClick={() => onFilterChange(activeFilter === "Trip Cancelled" ? "All" : "Trip Cancelled")}
        >
          Trip Cancelled
        </span>
      </div>
    </div>
  );
};
